import React, { useEffect, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';
import { Bell, Check, X, Calendar, MessageSquare, Clock, Tag, FileText, Plus, Loader2 } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { propsFundoModal } from '../../../src/lib/backdropModal';

interface LembretesViewerProps {
    uc_id?: string | number;
    uc_codigo?: string;
    mes_referencia?: string; // e.g. "MAI/2026"
    onUpdate?: () => void;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const parseMesReferencia = (mesRef: string) => {
    if (!mesRef) return null;
    const parts = mesRef.split('/');
    if (parts.length !== 2) return null;
    const mesIndex = MESES.indexOf(parts[0].toUpperCase());
    const ano = parseInt(parts[1], 10);
    if (mesIndex === -1 || isNaN(ano)) return null;
    
    // First day of the month
    const start = new Date(ano, mesIndex, 1);
    // Last day of the month
    const end = new Date(ano, mesIndex + 1, 0);
    
    return { start, end };
};

const LembretesViewer: React.FC<LembretesViewerProps> = ({ uc_id, uc_codigo, mes_referencia, onUpdate }) => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [lembretes, setLembretes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal States
    const [actionLoading, setActionLoading] = useState(false);
    const [descarteModal, setDescarteModal] = useState<{ isOpen: boolean; lembreteId: string | null }>({ isOpen: false, lembreteId: null });
    const [motivoDescarte, setMotivoDescarte] = useState('');

    const fetchLembretes = async () => {
        setLoading(true);
        try {
            let resolvedUcId = uc_id;
            
            // Resolves uc_id from uc_codigo if not provided
            if (!resolvedUcId && uc_codigo) {
                const { data: ucList } = await supabase
                    .from('unidades_consumidoras')
                    .select('id')
                    .or(`numero_uc.eq.${uc_codigo},outro_numero_uc.eq.${uc_codigo}`);
                
                if (ucList && ucList.length > 0) {
                    resolvedUcId = ucList[0].id;
                }
            }

            let query = supabase
                .from('lembretes_faturamento')
                .select('*')
                .eq('status', 'pendente')
                .order('created_at', { ascending: false });

            if (resolvedUcId && uc_codigo) {
                query = query.or(`uc_id.eq.${resolvedUcId},uc_codigo.eq.${uc_codigo}`);
            } else if (resolvedUcId) {
                query = query.eq('uc_id', resolvedUcId);
            } else if (uc_codigo) {
                query = query.eq('uc_codigo', uc_codigo);
            } else {
                setLembretes([]);
                setLoading(false);
                return;
            }

            const { data, error } = await query;

            if (error) throw error;

            let filtered = data || [];

            // Se mes_referencia foi passado, filtra apenas os que se aplicam
            if (mes_referencia) {
                const targetDates = parseMesReferencia(mes_referencia);
                if (targetDates) {
                    filtered = filtered.filter(l => {
                        // Aplica se for o mesmo mês exato
                        if (l.mes_referencia === mes_referencia) return true;
                        
                        // Aplica se o mes_referencia alvo cair dentro do período do lembrete
                        if (l.data_inicio && l.data_fim) {
                            const lStart = new Date(l.data_inicio);
                            const lEnd = new Date(l.data_fim);
                            // Se a data de fim do lembrete >= inicio do mês alvo, e data inicio <= fim do mes alvo
                            if (lEnd >= targetDates.start && lStart <= targetDates.end) {
                                return true;
                            }
                        }
                        return false;
                    });
                }
            }

            setLembretes(filtered);
        } catch (err) {
            console.error('Erro ao buscar lembretes:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (uc_id || uc_codigo) {
            fetchLembretes();
        }
    }, [uc_id, uc_codigo, mes_referencia]);

    const handleConcluir = async (id: string) => {
        if (!confirm('Deseja realmente marcar este lembrete como concluído?')) return;
        setActionLoading(true);
        try {
            const { error } = await supabase
                .from('lembretes_faturamento')
                .update({ status: 'concluido', data_conclusao: new Date() })
                .eq('id', id);
            
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'concluir_lembrete', origem: 'acao' });
            addToast('Lembrete concluído!', 'success');
            await fetchLembretes();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error(err);
            addToast('Erro ao concluir.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmDescarte = async () => {
        if (!motivoDescarte.trim()) {
            addToast('Digite um motivo.', 'error');
            return;
        }
        setActionLoading(true);
        try {
            const { error } = await supabase
                .from('lembretes_faturamento')
                .update({ status: 'descartado', motivo_descarte: motivoDescarte, data_conclusao: new Date() })
                .eq('id', descarteModal.lembreteId);
            
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'lembretes_cobranca', acao: 'descartar_lembrete', origem: 'acao' });
            addToast('Lembrete descartado!', 'success');
            setDescarteModal({ isOpen: false, lembreteId: null });
            setMotivoDescarte('');
            await fetchLembretes();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error(err);
            addToast('Erro ao descartar.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center p-4"><Loader2 className="animate-spin text-primary" size={24} /></div>;
    }

    if (lembretes.length === 0) return null;

    return (
        <div className="mb-6 space-y-3">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 font-bold px-1">
                <Bell size={20} className="animate-bounce" />
                <h2>Avisos e Lembretes Pendentes ({lembretes.length})</h2>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
                {lembretes.map(lembrete => {
                    const isDesconto = lembrete.tipo === 'desconto';
                    const isAcrescimo = lembrete.tipo === 'acrescimo';
                    
                    return (
                        <div key={lembrete.id} className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                            <div className="flex gap-4 items-start">
                                <div className={`p-2 rounded-lg mt-1 shrink-0 ${isDesconto ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : isAcrescimo ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                    {isDesconto ? <Tag size={20} /> : isAcrescimo ? <Plus size={20} /> : <FileText size={20} />}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${isDesconto ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200' : isAcrescimo ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' : 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'}`}>
                                            {lembrete.tipo}
                                        </span>
                                        <span className="text-xs font-medium text-orange-700/70 dark:text-orange-400/70 flex items-center gap-1">
                                            <Calendar size={12} />
                                            {lembrete.mes_referencia ? lembrete.mes_referencia : `${lembrete.data_inicio?.split('-').reverse().join('/')} até ${lembrete.data_fim?.split('-').reverse().join('/')}`}
                                        </span>
                                    </div>
                                    <p className="text-gray-800 dark:text-gray-200 mt-2 font-medium">{lembrete.descricao}</p>
                                    {lembrete.criado_por_nome && (
                                        <p className="text-xs text-orange-600/70 dark:text-orange-400/70 mt-1 font-semibold">
                                            Criado por: {lembrete.criado_por_nome}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
                                <button 
                                    onClick={() => handleConcluir(lembrete.id)}
                                    disabled={actionLoading}
                                    className="px-3 py-1.5 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors"
                                >
                                    <Check size={16} /> Concluir
                                </button>
                                <button 
                                    onClick={() => setDescarteModal({ isOpen: true, lembreteId: lembrete.id })}
                                    disabled={actionLoading}
                                    className="px-3 py-1.5 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-semibold text-sm rounded-lg flex items-center gap-1 transition-colors"
                                >
                                    <X size={16} /> Descartar
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal de Descarte */}
            {descarteModal.isOpen && (
                <div
                    className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
                    {...propsFundoModal(() => { setDescarteModal({ isOpen: false, lembreteId: null }); setMotivoDescarte(''); })}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-scale-in p-6"
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Motivo do Descarte</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Por que este lembrete está sendo descartado?</p>
                        <textarea
                            value={motivoDescarte}
                            onChange={(e) => setMotivoDescarte(e.target.value)}
                            placeholder="Descreva o motivo..."
                            className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 outline-none resize-none mb-4"
                            rows={3}
                        />
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => {
                                    setDescarteModal({ isOpen: false, lembreteId: null });
                                    setMotivoDescarte('');
                                }}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleConfirmDescarte}
                                disabled={actionLoading || !motivoDescarte.trim()}
                                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                            >
                                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : 'Confirmar Descarte'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LembretesViewer;
